import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react'
import {
  fetchProfile,
  upsertProfile,
  redeemInviteCode as redeemInviteCodeQuery,
} from '@/lib/queries/auth'
import { markSeen } from '@/lib/queries/engagement'
import { FREE_TIER_MESSAGE_LIMIT, INVITE_CODE_ERROR_MESSAGES } from '@/lib/constants'
import { PROFILE_DEFAULTS } from '@/lib/defaults'
import { subscribeToMailerLite, MAILERLITE_INTENTS } from '@/lib/mailerlite'
import { useSession } from './SessionContext'

const ProfileContext = createContext(null)

export function useProfile() {
  const ctx = useContext(ProfileContext)
  if (!ctx) throw new Error('useProfile must be used within a ProfileProvider')
  return ctx
}

function errorMessageFrom(err) {
  return err?.message || 'Something went wrong.'
}

export function ProfileProvider({ children }) {
  // Inner-hook-call pattern — ProfileContext consumes SessionContext directly
  // instead of prop-drilling user through AuthProviders.
  const { user } = useSession()
  const [profile, setProfile] = useState(null)
  const updateProfileBusyRef = useRef(false)
  // Track the last user id we called markSeen for. Supabase refreshes can
  // replace the user object identity without a real sign-in; this ref keeps
  // markSeen firing once per actual user switch instead of once per object.
  const lastSeenUserIdRef = useRef(null)

  // Fetch profile whenever user changes. Profile rows are created by the
  // handle_new_user trigger on auth.users for both email and Google OAuth
  // signups, so fetchProfile always finds a row. We still backfill
  // display_name from Google metadata if the trigger missed it (e.g. user
  // created before the trigger existed). When user clears (sign-out or
  // SIGNED_OUT listener event), profile clears too.
  useEffect(() => {
    if (!user) {
      setProfile(null)
      lastSeenUserIdRef.current = null
      return
    }
    let cancelled = false
    fetchProfile(user.id)
      .then(async p => {
        if (cancelled) return

        if (p && !p.display_name && user.user_metadata?.full_name) {
          await upsertProfile(user.id, { display_name: user.user_metadata.full_name })
          p = { ...p, display_name: user.user_metadata.full_name }
        }
        if (!cancelled) setProfile(p)
      })
      .catch(error => {
        console.error('Failed to sync auth profile:', error)
      })
    if (lastSeenUserIdRef.current !== user.id) {
      lastSeenUserIdRef.current = user.id
      markSeen(user.id)
    }
    return () => {
      cancelled = true
    }
  }, [user])

  const updateProfile = useCallback(
    async data => {
      if (!user) return { error: 'Not authenticated' }
      if (updateProfileBusyRef.current) return { error: 'Update already in progress' }
      updateProfileBusyRef.current = true
      try {
        const dbData = {}
        if (data.displayName !== undefined) dbData.display_name = data.displayName
        if (data.denomination !== undefined) dbData.denomination = data.denomination
        if (data.personalizeExperience !== undefined)
          dbData.personalize_experience = data.personalizeExperience
        if (data.spiritualMaturity !== undefined) dbData.spiritual_maturity = data.spiritualMaturity
        if (data.faithStruggles !== undefined) dbData.faith_struggles = data.faithStruggles
        if (data.preferredTranslation !== undefined)
          dbData.preferred_translation = data.preferredTranslation
        if (data.debatedTopicsView !== undefined)
          dbData.debated_topics_view = data.debatedTopicsView
        if (data.role !== undefined) dbData.role = data.role
        if (data.interests !== undefined) dbData.interests = data.interests
        if (data.responseDepth !== undefined) dbData.response_depth = data.responseDepth
        if (data.scriptureCitation !== undefined) dbData.scripture_citation = data.scriptureCitation
        if (data.sermonStyle !== undefined) dbData.sermon_style = data.sermonStyle
        if (data.sermonFormat !== undefined) dbData.sermon_format = data.sermonFormat
        if (data.highlightsEnabled !== undefined) dbData.highlights_enabled = data.highlightsEnabled
        if (data.bibleInsightLength !== undefined)
          dbData.bible_insight_length = data.bibleInsightLength
        if (data.primaryGoal !== undefined) dbData.primary_goal = data.primaryGoal
        if (data.scriptureUse !== undefined) dbData.scripture_use = data.scriptureUse
        if (data.referralSource !== undefined) dbData.referral_source = data.referralSource

        // V7 onboarding bridges (added 2026-05-17).
        // Spec: specs/v7-onboarding-production-migration.md Phase A.3
        // All map camelCase prop → snake_case DB column for the new
        // V7 step writes (current_struggles from step 3, age/days/minutes
        // from step 11 rhythm sliders, reminder + notifications from
        // step 13, streak triple from step 7).
        if (data.currentStruggles !== undefined) dbData.current_struggles = data.currentStruggles
        if (data.age !== undefined) dbData.age = data.age
        if (data.bibleReadingDaysPerWeek !== undefined)
          dbData.bible_reading_days_per_week = data.bibleReadingDaysPerWeek
        if (data.dailyTimeGoal !== undefined) dbData.daily_time_goal = data.dailyTimeGoal
        if (data.reminderMethod !== undefined) dbData.reminder_method = data.reminderMethod
        if (data.notificationPreferences !== undefined)
          dbData.notification_preferences = data.notificationPreferences
        if (data.streakCount !== undefined) dbData.streak_count = data.streakCount
        if (data.lastActiveDate !== undefined) dbData.last_active_date = data.lastActiveDate
        if (data.longestStreak !== undefined) dbData.longest_streak = data.longestStreak

        try {
          await upsertProfile(user.id, dbData)
          setProfile(prev => ({ ...prev, ...dbData }))
          return { error: null }
        } catch (err) {
          return { error: errorMessageFrom(err) }
        }
      } finally {
        updateProfileBusyRef.current = false
      }
    },
    [user]
  )

  const completeOnboarding = useCallback(async () => {
    if (!user) return { error: 'Not authenticated' }
    try {
      await upsertProfile(user.id, { onboarding_completed: true })
      setProfile(prev => ({ ...prev, onboarding_completed: true }))
      // Add the new user to the MailerLite Sign-up group so the Free
      // Welcome Drip fires. Fire-and-forget so a MailerLite outage never
      // blocks onboarding completion. Catches every error.
      if (user.email) {
        const displayName = profile?.display_name || profile?.displayName || ''
        subscribeToMailerLite(user.email, displayName, MAILERLITE_INTENTS.SIGN_UP).catch(err => {
          console.warn('Sign-up subscribe failed (non-blocking):', err)
        })
      }
      return { error: null }
    } catch (err) {
      return { error: errorMessageFrom(err) }
    }
  }, [user, profile])

  const redeemInviteCode = useCallback(
    async code => {
      try {
        const result = await redeemInviteCodeQuery(code)
        if (!result.success) {
          const reason = result.reason || 'invalid'
          return {
            error: INVITE_CODE_ERROR_MESSAGES[reason] || INVITE_CODE_ERROR_MESSAGES.invalid,
            success: false,
            reason,
          }
        }
        const p = await fetchProfile(user.id)
        if (p) setProfile(p)
        return { error: null, success: true, groupName: result.group_name || null }
      } catch (err) {
        return { error: errorMessageFrom(err), success: false }
      }
    },
    [user]
  )

  const refreshProfile = useCallback(async () => {
    if (!user) return
    const p = await fetchProfile(user.id)
    if (p) setProfile(p)
  }, [user])

  // Normalize snake_case DB fields to camelCase for components
  const normalizedProfile = useMemo(
    () =>
      profile
        ? {
            displayName: profile.display_name || '',
            denomination: profile.denomination || '',
            personalizeExperience: profile.personalize_experience || false,
            spiritualMaturity: profile.spiritual_maturity || '',
            faithStruggles: profile.faith_struggles || '',
            preferredTranslation:
              profile.preferred_translation || PROFILE_DEFAULTS.preferredTranslation,
            debatedTopicsView: profile.debated_topics_view || PROFILE_DEFAULTS.debatedTopicsView,
            role: profile.role || '',
            interests: profile.interests || [],
            isPremium: profile.is_premium || false,
            subscriptionPlan: profile.subscription_plan || null,
            isAdmin: profile.is_admin || false,
            onboardingCompleted: profile.onboarding_completed || false,
            responseDepth: profile.response_depth || PROFILE_DEFAULTS.responseDepth,
            scriptureCitation: profile.scripture_citation || PROFILE_DEFAULTS.scriptureCitation,
            sermonStyle: profile.sermon_style || PROFILE_DEFAULTS.sermonStyle,
            sermonFormat: profile.sermon_format || PROFILE_DEFAULTS.sermonFormat,
            highlightsEnabled: profile.highlights_enabled ?? PROFILE_DEFAULTS.highlightsEnabled,
            bibleInsightLength: profile.bible_insight_length || PROFILE_DEFAULTS.bibleInsightLength,
            primaryGoal: profile.primary_goal || '',
            scriptureUse: profile.scripture_use || [],
            referralSource: profile.referral_source || '',
            referralCode: profile.referral_code || null,
            referralCredits: profile.referral_credits || 0,
            monthlyMessagesUsed: profile.monthly_messages_used || 0,
            // V7 onboarding fields (read mappings added 2026-05-17 to mirror
            // the new bridges in updateProfile above). All fall back to
            // null/[] when absent so components handle pre-V7 users cleanly.
            currentStruggles: profile.current_struggles || [],
            age: profile.age ?? null,
            bibleReadingDaysPerWeek: profile.bible_reading_days_per_week ?? null,
            dailyTimeGoal: profile.daily_time_goal ?? null,
            reminderMethod: profile.reminder_method || null,
            notificationPreferences: profile.notification_preferences || null,
            streakCount: profile.streak_count || 0,
            longestStreak: profile.longest_streak || 0,
            lastActiveDate: profile.last_active_date || null,
            lastChatTab: profile.last_chat_tab || null,
            lastBibleBook: profile.last_bible_book || null,
            lastBibleChapter: profile.last_bible_chapter || null,
            homeDashboardFirstVisitAt: profile.home_dashboard_first_visit_at || null,
            onboardingNextStepSeenAt: profile.onboarding_next_step_seen_at || null,
            trialExpired: (() => {
              if (profile.is_premium) return false
              return (profile.monthly_messages_used || 0) >= FREE_TIER_MESSAGE_LIMIT
            })(),
          }
        : null,
    [profile]
  )

  const value = useMemo(
    () => ({
      profile: normalizedProfile,
      rawProfile: profile,
      updateProfile,
      completeOnboarding,
      redeemInviteCode,
      refreshProfile,
    }),
    [
      normalizedProfile,
      profile,
      updateProfile,
      completeOnboarding,
      redeemInviteCode,
      refreshProfile,
    ]
  )

  return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>
}

ProfileProvider.displayName = 'ProfileProvider'
